// This is a patch to fix the empty message issue in the core logger
// Apply these changes directly in src/core/constants.ts

import type { ConsolaOptions, ConsolaReporter } from "consola";

export const LOG_LEVELS = {
  // Control levels
  SILENT: -999,
  VERBOSE: 999,

  // Error levels
  FATAL: 0,
  ERROR: 0,

  // Standard levels
  WARN: 1,
  LOG: 2,

  // Info levels
  INFO: 3,
  SUCCESS: 3,
  READY: 3,
  START: 3,

  // Debug levels
  DEBUG: 4,
  TRACE: 5,
} as const;

// Updated fixed reporter that ensures message is never empty
const consoleReporter: ConsolaReporter = {
  log: (logObj, ctx) => {
    // Extract the primary properties
    const { args = [], date, type, tag } = logObj;

    // Get message from logObj or first argument
    let message;
    let processedArgs = [...args];

    // 1. If we have a message in the logObj, use it
    if (typeof logObj.message === "string" && logObj.message.length > 0) {
      message = logObj.message;

      // If the first arg is the same as the message, remove it to avoid duplication
      if (args.length > 0 && args[0] === message) {
        processedArgs = args.slice(1);
      }
    }
    // 2. Otherwise, if the first argument is a string, use it as the message
    else if (args.length > 0 && typeof args[0] === "string") {
      message = args[0];
      processedArgs = args.slice(1);
    }
    // 3. Fallback to a placeholder
    else {
      message = "[No message]";
    }

    // Extract errors from the args
    const error = args.find((arg) => arg instanceof Error);
    const errorStr = error ? ` Error: ${error.message}` : "";

    // Filter out errors from the processed args
    const nonErrorArgs = processedArgs.filter((arg) => !(arg instanceof Error));

    // Format timestamp
    const timestamp =
      date instanceof Date ? date.toISOString() : new Date().toISOString();

    // Add tag if present
    const tagStr = tag ? `[${tag}] ` : "";

    // Map type to console method
    const method =
      type === "error" || type === "fatal"
        ? "error"
        : type === "warn"
          ? "warn"
          : type === "info" || type === "success"
            ? "info"
            : type === "debug" || type === "trace"
              ? "debug"
              : "log";

    // Format metadata if available
    let metadataStr = "";
    if (nonErrorArgs.length > 0) {
      try {
        metadataStr =
          " " +
          JSON.stringify(
            nonErrorArgs.length === 1 ? nonErrorArgs[0] : nonErrorArgs
          );
      } catch (e) {
        metadataStr = " [Cannot stringify metadata]";
      }
    }

    // Log to console with formatting
    console[method](
      `[${timestamp}] [${type.toUpperCase()}] ${tagStr}${message}${errorStr}${metadataStr}`
    );
  },
};

export const DEFAULT_OPTIONS: Partial<ConsolaOptions> = {
  level: 3,
  // Use the fixed reporter that ensures messages are properly displayed
  reporters: [consoleReporter],
  formatOptions: {
    date: true,
    colors: true,
    compact: false,
  },
};

export const ENV = {
  development: "development",
  production: "production",
  test: "test",
} as const;

// Export type helpers
export type LogLevel = keyof typeof LOG_LEVELS;
export type Environment = keyof typeof ENV;
